import type { RgbaFrame } from './types';

function ensurePositiveScale(scale: number): number {
  if (!Number.isFinite(scale) || scale <= 0) return 1;
  return scale;
}

/** File / Blob → ImageBitmap */
export async function decodeToBitmap(file: Blob): Promise<ImageBitmap> {
  try {
    return await createImageBitmap(file);
  } catch {
    // 部分格式回退 HTMLImageElement
    const url = URL.createObjectURL(file);
    try {
      const img = await loadHtmlImage(url);
      return await createImageBitmap(img);
    } finally {
      URL.revokeObjectURL(url);
    }
  }
}

function loadHtmlImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('图片解码失败'));
    img.src = url;
  });
}

/**
 * 将 bitmap 缩放到目标尺寸并抽取 RGBA。
 * scale 相对原图；若指定 targetW/H 则优先（封面 fit）。
 */
export function bitmapToRgba(
  bitmap: ImageBitmap,
  options: { scale?: number; targetWidth?: number; targetHeight?: number } = {},
): RgbaFrame {
  const scale = ensurePositiveScale(options.scale ?? 1);
  const tw =
    options.targetWidth ??
    Math.max(1, Math.round(bitmap.width * scale));
  const th =
    options.targetHeight ??
    Math.max(1, Math.round(bitmap.height * scale));

  const canvas = document.createElement('canvas');
  canvas.width = tw;
  canvas.height = th;
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  if (!ctx) throw new Error('无法创建 Canvas 2D 上下文');

  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  ctx.clearRect(0, 0, tw, th);
  ctx.drawImage(bitmap, 0, 0, tw, th);

  const imageData = ctx.getImageData(0, 0, tw, th);
  // 拷贝一份，避免 canvas 被回收后 buffer 失效
  const data = new Uint8Array(imageData.data.buffer.slice(0));
  return { width: tw, height: th, data };
}

export async function fileToRgba(
  file: Blob,
  options: { scale?: number; targetWidth?: number; targetHeight?: number } = {},
): Promise<RgbaFrame> {
  const bitmap = await decodeToBitmap(file);
  try {
    return bitmapToRgba(bitmap, options);
  } finally {
    bitmap.close();
  }
}
